// ScheduleCalendar.jsx

import {
  Alert,
  Box,
  CircularProgress,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { CLUB_ID, db } from "../services/firebase";
import React, { useEffect, useState } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";

import CalendarView from "./CalendarView";
import ListView from "./CalendarListView";
import { useTheme } from "@mui/material/styles";

const ScheduleCalendar = ({ month, year }) => {
  const clubId = CLUB_ID; // Replace with your actual club ID or pass it as a prop
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scheduleData, setScheduleData] = useState(null);
  const [refresh, setRefresh] = useState(false); // State to trigger refresh
  const [availableHosts] = useState([
    "Lulu",
    "Brennan",
    "Jason",
    "Lantz",
    "Michelle",
    "Cindy Sue",
    "Marian",
    "Bam",
    "Coverage Needed",
  ]);

  // Get the month name
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const monthName = monthNames[month - 1];

  // Function to update a shift in Firestore
  const updateShift = async (day, shiftKey, newValue) => {
    if (!scheduleData) return;

    try {
      // Find the day to update
      const updatedDays = scheduleData.days.map((d) => {
        if (d.day === day) {
          return {
            ...d,
            [shiftKey]: {
              name: newValue.name,
              alternate: newValue.alternate || false,
            },
          };
        }
        return d;
      });

      // Create updated schedule data
      const updatedSchedule = {
        ...scheduleData,
        days: updatedDays,
      };

      // Update local state first for immediate UI update
      setScheduleData(updatedSchedule);

      // Update Firestore
      const scheduleRef = doc(
        db,
        "clubs",
        clubId,
        "schedule",
        `${year}-${month.toString().padStart(2, "0")}`
      );
      await updateDoc(scheduleRef, updatedSchedule);
    } catch (err) {
      console.error("Error updating shift:", err);
      setError("Failed to update shift. Please try again.");
      // Reload data to revert changes if update failed
      setRefresh((prev) => !prev);
    }
  };

  // Load data when component mounts or when month/year changes
  useEffect(() => {
    // Function to load schedule data from Firestore
    const loadScheduleData = async () => {
      setLoading(true);
      try {
        // Create a document reference for the specific month and year
        const scheduleRef = doc(
          db,
          "clubs",
          clubId,
          "schedule",
          `${year}-${month.toString().padStart(2, "0")}`
        );
        const scheduleSnapshot = await getDoc(scheduleRef);

        if (scheduleSnapshot.exists()) {
          setScheduleData(scheduleSnapshot.data());
        } else {
          // If no data exists, create an empty schedule
          const daysInMonth = new Date(year, month, 0).getDate();
          const emptySchedule = {
            month,
            year,
            days: Array.from({ length: daysInMonth }, (_, i) => ({
              day: i + 1,
              shift1: { name: "" },
              shift2: { name: "" },
            })),
          };
          setScheduleData(emptySchedule);
        }

        setError(null);
      } catch (err) {
        console.error("Error loading schedule:", err);
        setError("Failed to load schedule data. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    loadScheduleData();
  }, [clubId, month, year, refresh]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!scheduleData) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">No schedule data available.</Alert>
      </Box>
    );
  }

  // Render the appropriate view based on screen size
  return (
    <Box sx={{ maxWidth: "100%", mx: "auto" }}>
      <Typography variant="h4" component="h1" align="center" gutterBottom>
        The Cola Crew Schedule
      </Typography>
      <Typography variant="h5" component="h2" align="center" gutterBottom>
        {monthName} {year}
      </Typography>

      {isMobile ? (
        <ListView
          scheduleData={scheduleData}
          availableHosts={availableHosts}
          onUpdateShift={updateShift}
        />
      ) : (
        <CalendarView
          scheduleData={scheduleData}
          availableHosts={availableHosts}
          onUpdateShift={updateShift}
        />
      )}

      <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          ** Indicates temporary shift coverage.
        </Typography>
      </Box>
      <Box
        sx={{
          mb: 3,
          textAlign: "center",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box sx={{ mr: 2 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Other Days
          </Typography>
          <Typography variant="body1">
            <strong>Shift 1:</strong> 10:45 AM-5:15 PM
          </Typography>
          <Typography variant="body1">
            <strong>Shift 2:</strong> 5:00 PM-11:15 PM
          </Typography>
        </Box>
        <Box>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Mon & Fri{" "}
          </Typography>
          <Typography variant="body1">
            <strong>Shift 1:</strong> 1:45 AM-6:15 PM
          </Typography>
          <Typography variant="body1">
            <strong>Shift 2:</strong> 6:00 PM-11:15 PM
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ScheduleCalendar;
